import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Navigate } from "react-router-dom";
import DashboardLayout from "../components/Layout/DashboardLayout";
import { TableSkeletonRows } from "../components/ui/Skeleton";
import { clientsApi } from "../api/clientsApi";
import {
  staffApi,
  StaffMember,
  StaffClientSummary,
  InviteStaffRequest,
  CreateStaffRequest,
  staffApiErrorCode,
  staffApiErrorMessage,
} from "../api/staffApi";
import { Client } from "../types";
import { useAuth } from "../hooks/useAuth";
import { ConfirmModal } from "../components/shared/ConfirmModal";
import { UpgradeLimitModal } from "../components/shared/UpgradeLimitModal";

const EDIT_ROLES = ["Admin", "Advisor", "Support"] as const;
const CREATE_ROLES = ["Advisor", "Support"] as const;

function roleBadgeClass(role: string): string {
  const r = role.toLowerCase();
  if (r === "admin") {
    return "border-violet-500/50 bg-violet-500/15 text-violet-200";
  }
  if (r === "advisor") {
    return "border-sky-500/50 bg-sky-500/15 text-sky-200";
  }
  if (r === "support") {
    return "border-teal-500/50 bg-teal-500/15 text-teal-200";
  }
  return "border-slate-600 bg-slate-800 text-slate-300";
}

function statusBadgeClass(isActive: boolean): string {
  return isActive
    ? "border-emerald-500/40 bg-emerald-500/15 text-emerald-300"
    : "border-rose-500/40 bg-rose-500/15 text-rose-300";
}

function formatDate(iso: string | null | undefined): string {
  if (!iso) {
    return "—";
  }
  try {
    return new Date(iso).toLocaleDateString(undefined, { dateStyle: "medium" });
  } catch {
    return "—";
  }
}

function isCurrentUser(member: StaffMember, user: { userId?: string; operatorEmail?: string } | null): boolean {
  if (!user) {
    return false;
  }
  if (user.userId && member.id === user.userId) {
    return true;
  }
  return member.email.toLowerCase() === (user.operatorEmail ?? "").toLowerCase();
}

const StaffPage: React.FC = () => {
  const { user } = useAuth();
  const isAdmin = (user?.role ?? "").toLowerCase() === "admin";

  const [staff, setStaff] = useState<StaffMember[]>([]);
  const [clients, setClients] = useState<Client[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [addOpen, setAddOpen] = useState(false);
  const [addForm, setAddForm] = useState<CreateStaffRequest>({
    firstName: "",
    lastName: "",
    email: "",
    password: "",
    role: "Advisor",
  });
  const [addSubmitting, setAddSubmitting] = useState(false);

  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviteForm, setInviteForm] = useState<InviteStaffRequest>({
    firstName: "",
    lastName: "",
    email: "",
    role: "Advisor",
  });
  const [inviteSubmitting, setInviteSubmitting] = useState(false);

  const [editing, setEditing] = useState<StaffMember | null>(null);
  const [editFirstName, setEditFirstName] = useState("");
  const [editLastName, setEditLastName] = useState("");
  const [editRole, setEditRole] = useState("");
  const [editActive, setEditActive] = useState(true);
  const [editSubmitting, setEditSubmitting] = useState(false);

  const [deactivateTarget, setDeactivateTarget] = useState<StaffMember | null>(null);
  const [seatLimitModalOpen, setSeatLimitModalOpen] = useState(false);

  const [assignStaff, setAssignStaff] = useState<StaffMember | null>(null);
  const [assignedClients, setAssignedClients] = useState<StaffClientSummary[]>([]);
  const [assignClientId, setAssignClientId] = useState("");
  const [assignLoading, setAssignLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [s, c] = await Promise.all([staffApi.list(), clientsApi.getAll()]);
      setStaff(s);
      setClients(c);
    } catch {
      setError("Could not load staff. Try again.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!user || !isAdmin) {
      return;
    }
    void load();
  }, [load, user, isAdmin]);

  useEffect(() => {
    if (!assignStaff) {
      setAssignedClients([]);
      setAssignClientId("");
      return;
    }
    let cancelled = false;
    void (async () => {
      setAssignLoading(true);
      try {
        const list = await staffApi.getClients(assignStaff.id);
        if (!cancelled) {
          setAssignedClients(list);
        }
      } catch {
        if (!cancelled) {
          setAssignedClients([]);
        }
      } finally {
        if (!cancelled) {
          setAssignLoading(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [assignStaff]);

  const clientsForAssign = useMemo(() => {
    if (!assignStaff) {
      return [];
    }
    const assignedIds = new Set(assignedClients.map((x) => x.id));
    return clients.filter((c) => !assignedIds.has(c.id));
  }, [assignStaff, assignedClients, clients]);

  const openEdit = (m: StaffMember) => {
    setEditing(m);
    setEditFirstName(m.firstName);
    setEditLastName(m.lastName);
    setEditRole(m.role);
    const self = isCurrentUser(m, user);
    setEditActive(self ? true : m.isActive);
  };

  const handleAddStaff = async (e: React.FormEvent) => {
    e.preventDefault();
    if (
      !addForm.firstName.trim() ||
      !addForm.lastName.trim() ||
      !addForm.email.trim() ||
      addForm.password.length < 8
    ) {
      return;
    }
    setAddSubmitting(true);
    setError(null);
    try {
      await staffApi.create({
        ...addForm,
        firstName: addForm.firstName.trim(),
        lastName: addForm.lastName.trim(),
        email: addForm.email.trim(),
      });
      setAddOpen(false);
      setAddForm({ firstName: "", lastName: "", email: "", password: "", role: "Advisor" });
      await load();
    } catch (err: unknown) {
      if (staffApiErrorCode(err) === "TEAM_SEAT_LIMIT_REACHED") {
        setSeatLimitModalOpen(true);
        setError(null);
      } else {
        setError(staffApiErrorMessage(err) ?? "Could not add staff. Check email and password.");
      }
    } finally {
      setAddSubmitting(false);
    }
  };

  const handleInvite = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!inviteForm.firstName.trim() || !inviteForm.lastName.trim() || !inviteForm.email.trim()) {
      return;
    }
    setInviteSubmitting(true);
    setError(null);
    try {
      await staffApi.invite({
        ...inviteForm,
        firstName: inviteForm.firstName.trim(),
        lastName: inviteForm.lastName.trim(),
        email: inviteForm.email.trim(),
      });
      setInviteOpen(false);
      setInviteForm({ firstName: "", lastName: "", email: "", role: "Advisor" });
      await load();
    } catch (err: unknown) {
      if (staffApiErrorCode(err) === "TEAM_SEAT_LIMIT_REACHED") {
        setSeatLimitModalOpen(true);
        setError(null);
      } else {
        setError(staffApiErrorMessage(err) ?? "Invite failed. Check email and permissions.");
      }
    } finally {
      setInviteSubmitting(false);
    }
  };

  const handleEditSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editing) {
      return;
    }
    setEditSubmitting(true);
    setError(null);
    try {
      await staffApi.update(editing.id, {
        firstName: editFirstName.trim(),
        lastName: editLastName.trim(),
        role: editRole,
        isActive: editActive,
      });
      setEditing(null);
      await load();
    } catch (err: unknown) {
      setError(staffApiErrorMessage(err) ?? "Update failed.");
    } finally {
      setEditSubmitting(false);
    }
  };

  const openDeactivateConfirm = (member: StaffMember) => {
    if (!member.isActive || isCurrentUser(member, user)) {
      return;
    }
    setDeactivateTarget(member);
  };

  const confirmDeactivate = async () => {
    const member = deactivateTarget;
    setDeactivateTarget(null);
    if (!member) {
      return;
    }
    setError(null);
    try {
      await staffApi.deactivate(member.id);
      await load();
    } catch (err: unknown) {
      setError(staffApiErrorMessage(err) ?? "Could not deactivate staff (e.g. last admin).");
    }
  };

  const handleActivate = async (member: StaffMember) => {
    if (member.isActive) {
      return;
    }
    setError(null);
    try {
      await staffApi.update(member.id, { isActive: true });
      await load();
    } catch (err: unknown) {
      setError(staffApiErrorMessage(err) ?? "Could not activate staff.");
    }
  };

  const handleAssign = async () => {
    if (!assignStaff || !assignClientId) {
      return;
    }
    setError(null);
    try {
      await staffApi.assignClient(assignStaff.id, assignClientId);
      setAssignClientId("");
      const list = await staffApi.getClients(assignStaff.id);
      setAssignedClients(list);
      await load();
    } catch {
      setError("Assign failed. Ensure the staff member is active.");
    }
  };

  if (!user || !isAdmin) {
    return <Navigate to="/dashboard" replace />;
  }

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div className="flex flex-col justify-between gap-3 sm:flex-row sm:items-center">
          <div>
            <h1 className="text-2xl font-semibold text-white">Staff</h1>
            <p className="text-sm text-slate-400">Manage team members, roles, and client assignments.</p>
          </div>
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => setAddOpen(true)}
              className="rounded-lg bg-emerald-600 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-500"
            >
              Add staff
            </button>
            <button
              type="button"
              onClick={() => setInviteOpen(true)}
              className="rounded-lg border border-slate-600 bg-slate-800 px-4 py-2 text-sm font-semibold text-slate-200 hover:bg-slate-700"
            >
              Invite by email
            </button>
          </div>
        </div>

        {error && (
          <div className="rounded-lg border border-rose-500/40 bg-rose-950/40 px-4 py-3 text-sm text-rose-200">
            {error}
          </div>
        )}

        <div className="overflow-hidden rounded-xl border border-slate-800 bg-slate-900/80 shadow-lg shadow-black/20">
          {loading ? (
            <div className="space-y-3 p-6" aria-busy="true" aria-label="Loading staff">
              <TableSkeletonRows rows={6} cols={7} />
            </div>
          ) : staff.length === 0 ? (
            <div className="p-10 text-center">
              <p className="text-slate-300">No staff members yet.</p>
              <p className="mt-2 text-sm text-slate-500">Add your first teammate with a password, or send an email invite.</p>
              <button
                type="button"
                onClick={() => setAddOpen(true)}
                className="mt-6 rounded-lg bg-emerald-600 px-5 py-2.5 text-sm font-semibold text-white hover:bg-emerald-500"
              >
                Add staff
              </button>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full text-left text-sm">
                <thead>
                  <tr className="border-b border-slate-800 bg-slate-950/60 text-xs uppercase tracking-wide text-slate-500">
                    <th className="px-4 py-3 font-medium">Name</th>
                    <th className="px-4 py-3 font-medium">Email</th>
                    <th className="px-4 py-3 font-medium">Role</th>
                    <th className="px-4 py-3 font-medium">Status</th>
                    <th className="px-4 py-3 font-medium">Created</th>
                    <th className="px-4 py-3 font-medium">Last login</th>
                    <th className="px-4 py-3 font-medium">Clients</th>
                    <th className="px-4 py-3 text-right font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-800">
                  {staff.map((m) => {
                    const self = isCurrentUser(m, user);
                    const inactiveRow = !m.isActive;
                    return (
                      <tr
                        key={m.id}
                        className={`text-slate-200 hover:bg-slate-800/40 ${inactiveRow ? "opacity-70" : ""}`}
                      >
                        <td className="px-4 py-3 font-medium text-white">
                          {m.firstName} {m.lastName}
                        </td>
                        <td className="px-4 py-3 text-slate-400">{m.email}</td>
                        <td className="px-4 py-3">
                          <span
                            className={`inline-flex rounded-full border px-2.5 py-0.5 text-xs font-semibold ${roleBadgeClass(
                              m.role
                            )}`}
                          >
                            {m.role}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <span
                            className={`inline-flex rounded-full border px-2.5 py-0.5 text-xs font-semibold ${statusBadgeClass(
                              m.isActive
                            )}`}
                          >
                            {m.isActive ? "Active" : "Inactive"}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-slate-400">{formatDate(m.createdAt)}</td>
                        <td className="px-4 py-3 text-slate-400">{formatDate(m.lastLoginAt)}</td>
                        <td className="px-4 py-3 tabular-nums text-slate-300">{m.clientCount}</td>
                        <td className="px-4 py-3 text-right">
                          <div className="flex flex-wrap justify-end gap-2">
                            <button
                              type="button"
                              onClick={() => openEdit(m)}
                              className="rounded-lg border border-slate-600 bg-slate-800 px-3 py-1.5 text-xs font-medium text-slate-200 hover:border-slate-500 hover:bg-slate-700"
                            >
                              Edit
                            </button>
                            {m.isActive && (
                              <button
                                type="button"
                                onClick={() => setAssignStaff(m)}
                                className="rounded-lg border border-slate-600 bg-slate-800 px-3 py-1.5 text-xs font-medium text-slate-200 hover:border-slate-500 hover:bg-slate-700"
                              >
                                Assign clients
                              </button>
                            )}
                            {!m.isActive && (
                              <button
                                type="button"
                                onClick={() => void handleActivate(m)}
                                className="rounded-lg border border-emerald-500/40 bg-emerald-950/30 px-3 py-1.5 text-xs font-medium text-emerald-200 hover:bg-emerald-950/50"
                              >
                                Activate
                              </button>
                            )}
                            <button
                              type="button"
                              onClick={() => openDeactivateConfirm(m)}
                              disabled={!m.isActive || self}
                              title={self ? "You cannot deactivate your own account" : undefined}
                              className="rounded-lg border border-rose-500/40 bg-rose-950/30 px-3 py-1.5 text-xs font-medium text-rose-200 hover:bg-rose-950/50 disabled:cursor-not-allowed disabled:opacity-40"
                            >
                              Deactivate
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {addOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4 backdrop-blur-sm"
          role="dialog"
          aria-modal="true"
          aria-labelledby="add-staff-title"
          onClick={() => setAddOpen(false)}
          onKeyDown={(e) => e.key === "Escape" && setAddOpen(false)}
        >
          <div
            className="w-full max-w-md rounded-xl border border-slate-700 bg-slate-900 p-6 shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <h2 id="add-staff-title" className="text-lg font-semibold text-white">
              Add staff member
            </h2>
            <p className="mt-1 text-sm text-slate-400">Creates an account with the password you set (min. 8 characters).</p>
            <form onSubmit={(e) => void handleAddStaff(e)} className="mt-6 space-y-4">
              <div>
                <label className="block text-xs font-medium text-slate-400">First name</label>
                <input
                  required
                  value={addForm.firstName}
                  onChange={(e) => setAddForm((f) => ({ ...f, firstName: e.target.value }))}
                  className="mt-1 w-full rounded-lg border border-slate-700 bg-slate-800 px-3 py-2 text-sm text-white outline-none ring-blue-500 focus:ring-2"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-400">Last name</label>
                <input
                  required
                  value={addForm.lastName}
                  onChange={(e) => setAddForm((f) => ({ ...f, lastName: e.target.value }))}
                  className="mt-1 w-full rounded-lg border border-slate-700 bg-slate-800 px-3 py-2 text-sm text-white outline-none ring-blue-500 focus:ring-2"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-400">Email</label>
                <input
                  required
                  type="email"
                  value={addForm.email}
                  onChange={(e) => setAddForm((f) => ({ ...f, email: e.target.value }))}
                  className="mt-1 w-full rounded-lg border border-slate-700 bg-slate-800 px-3 py-2 text-sm text-white outline-none ring-blue-500 focus:ring-2"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-400">Password</label>
                <input
                  required
                  type="password"
                  minLength={8}
                  autoComplete="new-password"
                  value={addForm.password}
                  onChange={(e) => setAddForm((f) => ({ ...f, password: e.target.value }))}
                  className="mt-1 w-full rounded-lg border border-slate-700 bg-slate-800 px-3 py-2 text-sm text-white outline-none ring-blue-500 focus:ring-2"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-400">Role</label>
                <select
                  value={addForm.role}
                  onChange={(e) => setAddForm((f) => ({ ...f, role: e.target.value }))}
                  className="mt-1 w-full rounded-lg border border-slate-700 bg-slate-800 px-3 py-2 text-sm text-white outline-none ring-blue-500 focus:ring-2"
                >
                  {CREATE_ROLES.map((r) => (
                    <option key={r} value={r}>
                      {r}
                    </option>
                  ))}
                </select>
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <button
                  type="button"
                  onClick={() => setAddOpen(false)}
                  className="rounded-lg border border-slate-600 px-4 py-2 text-sm font-medium text-slate-300 hover:bg-slate-800"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={addSubmitting}
                  className="rounded-lg bg-emerald-600 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-500 disabled:opacity-50"
                >
                  {addSubmitting ? "Saving…" : "Add staff"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {inviteOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4 backdrop-blur-sm"
          role="dialog"
          aria-modal="true"
          aria-labelledby="invite-staff-title"
          onClick={() => setInviteOpen(false)}
          onKeyDown={(e) => e.key === "Escape" && setInviteOpen(false)}
        >
          <div
            className="w-full max-w-md rounded-xl border border-slate-700 bg-slate-900 p-6 shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <h2 id="invite-staff-title" className="text-lg font-semibold text-white">
              Invite staff member
            </h2>
            <p className="mt-1 text-sm text-slate-400">They will receive an email with a temporary password.</p>
            <form onSubmit={(e) => void handleInvite(e)} className="mt-6 space-y-4">
              <div>
                <label className="block text-xs font-medium text-slate-400">First name</label>
                <input
                  required
                  value={inviteForm.firstName}
                  onChange={(e) => setInviteForm((f) => ({ ...f, firstName: e.target.value }))}
                  className="mt-1 w-full rounded-lg border border-slate-700 bg-slate-800 px-3 py-2 text-sm text-white outline-none ring-blue-500 focus:ring-2"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-400">Last name</label>
                <input
                  required
                  value={inviteForm.lastName}
                  onChange={(e) => setInviteForm((f) => ({ ...f, lastName: e.target.value }))}
                  className="mt-1 w-full rounded-lg border border-slate-700 bg-slate-800 px-3 py-2 text-sm text-white outline-none ring-blue-500 focus:ring-2"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-400">Email</label>
                <input
                  required
                  type="email"
                  value={inviteForm.email}
                  onChange={(e) => setInviteForm((f) => ({ ...f, email: e.target.value }))}
                  className="mt-1 w-full rounded-lg border border-slate-700 bg-slate-800 px-3 py-2 text-sm text-white outline-none ring-blue-500 focus:ring-2"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-400">Role</label>
                <select
                  value={inviteForm.role}
                  onChange={(e) => setInviteForm((f) => ({ ...f, role: e.target.value }))}
                  className="mt-1 w-full rounded-lg border border-slate-700 bg-slate-800 px-3 py-2 text-sm text-white outline-none ring-blue-500 focus:ring-2"
                >
                  {CREATE_ROLES.map((r) => (
                    <option key={r} value={r}>
                      {r}
                    </option>
                  ))}
                </select>
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <button
                  type="button"
                  onClick={() => setInviteOpen(false)}
                  className="rounded-lg border border-slate-600 px-4 py-2 text-sm font-medium text-slate-300 hover:bg-slate-800"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={inviteSubmitting}
                  className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-500 disabled:opacity-50"
                >
                  {inviteSubmitting ? "Sending…" : "Send invite"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {editing && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4 backdrop-blur-sm"
          role="dialog"
          aria-modal="true"
          aria-labelledby="edit-staff-title"
          onClick={() => setEditing(null)}
          onKeyDown={(e) => e.key === "Escape" && setEditing(null)}
        >
          <div
            className="w-full max-w-md rounded-xl border border-slate-700 bg-slate-900 p-6 shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <h2 id="edit-staff-title" className="text-lg font-semibold text-white">
              Edit staff — {editing.firstName} {editing.lastName}
            </h2>
            <p className="mt-1 text-sm text-slate-400">Email cannot be changed here.</p>
            <form onSubmit={(e) => void handleEditSave(e)} className="mt-6 space-y-4">
              <div>
                <label className="block text-xs font-medium text-slate-400">First name</label>
                <input
                  required
                  value={editFirstName}
                  onChange={(e) => setEditFirstName(e.target.value)}
                  className="mt-1 w-full rounded-lg border border-slate-700 bg-slate-800 px-3 py-2 text-sm text-white outline-none ring-blue-500 focus:ring-2"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-400">Last name</label>
                <input
                  required
                  value={editLastName}
                  onChange={(e) => setEditLastName(e.target.value)}
                  className="mt-1 w-full rounded-lg border border-slate-700 bg-slate-800 px-3 py-2 text-sm text-white outline-none ring-blue-500 focus:ring-2"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-400">Role</label>
                <select
                  value={editRole}
                  onChange={(e) => setEditRole(e.target.value)}
                  className="mt-1 w-full rounded-lg border border-slate-700 bg-slate-800 px-3 py-2 text-sm text-white outline-none ring-blue-500 focus:ring-2"
                >
                  {EDIT_ROLES.map((r) => (
                    <option key={r} value={r}>
                      {r}
                    </option>
                  ))}
                </select>
              </div>
              <label className="flex cursor-pointer items-center gap-2 text-sm text-slate-300">
                <input
                  type="checkbox"
                  checked={editActive}
                  disabled={!!editing && isCurrentUser(editing, user)}
                  onChange={(e) => setEditActive(e.target.checked)}
                  className="rounded border-slate-600 bg-slate-800 disabled:opacity-50"
                />
                Active (can sign in)
                {editing && isCurrentUser(editing, user) ? (
                  <span className="text-xs text-slate-500">(You cannot deactivate yourself here.)</span>
                ) : null}
              </label>
              <div className="flex justify-end gap-2 pt-2">
                <button
                  type="button"
                  onClick={() => setEditing(null)}
                  className="rounded-lg border border-slate-600 px-4 py-2 text-sm font-medium text-slate-300 hover:bg-slate-800"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={editSubmitting}
                  className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-500 disabled:opacity-50"
                >
                  {editSubmitting ? "Saving…" : "Save changes"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {assignStaff && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4 backdrop-blur-sm"
          role="dialog"
          aria-modal="true"
          onClick={() => setAssignStaff(null)}
          onKeyDown={(e) => e.key === "Escape" && setAssignStaff(null)}
        >
          <div
            className="max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-xl border border-slate-700 bg-slate-900 p-6 shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <h2 className="text-lg font-semibold text-white">
              Assign clients — {assignStaff.firstName} {assignStaff.lastName}
            </h2>
            <p className="mt-1 text-sm text-slate-400">Link clients to this staff member.</p>

            <div className="mt-4">
              <p className="text-xs font-medium uppercase tracking-wide text-slate-500">Currently assigned</p>
              {assignLoading ? (
                <p className="mt-2 text-sm text-slate-500">Loading…</p>
              ) : assignedClients.length === 0 ? (
                <p className="mt-2 text-sm text-slate-500">None yet.</p>
              ) : (
                <ul className="mt-2 max-h-40 space-y-1 overflow-y-auto rounded-lg border border-slate-800 bg-slate-950/50 p-2">
                  {assignedClients.map((c) => (
                    <li key={c.id} className="text-sm text-slate-300">
                      {c.firstName} {c.lastName} <span className="text-slate-500">({c.email})</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            <div className="mt-6 space-y-2">
              <label className="block text-xs font-medium text-slate-400">Add client</label>
              <select
                value={assignClientId}
                onChange={(e) => setAssignClientId(e.target.value)}
                className="w-full rounded-lg border border-slate-700 bg-slate-800 px-3 py-2 text-sm text-white outline-none ring-blue-500 focus:ring-2"
              >
                <option value="">Select a client…</option>
                {clientsForAssign.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.firstName} {c.lastName} — {c.email}
                  </option>
                ))}
              </select>
              <div className="flex justify-end gap-2 pt-2">
                <button
                  type="button"
                  onClick={() => setAssignStaff(null)}
                  className="rounded-lg border border-slate-600 px-4 py-2 text-sm font-medium text-slate-300 hover:bg-slate-800"
                >
                  Close
                </button>
                <button
                  type="button"
                  disabled={!assignClientId}
                  onClick={() => void handleAssign()}
                  className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-500 disabled:opacity-40"
                >
                  Assign
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <UpgradeLimitModal
        open={seatLimitModalOpen}
        onClose={() => setSeatLimitModalOpen(false)}
        title="You've reached your team seat limit"
        description="Your current plan allows a limited number of active team members. Upgrade to Growth for up to 6 seats, or choose Agency for unlimited seats. Manage your subscription under Billing."
      />

      <ConfirmModal
        isOpen={deactivateTarget !== null}
        title="Deactivate Staff Member"
        message={
          deactivateTarget
            ? `This will prevent ${deactivateTarget.firstName} ${deactivateTarget.lastName} from logging in. You can reactivate them later.`
            : ""
        }
        confirmLabel="Deactivate"
        confirmVariant="warning"
        onConfirm={() => void confirmDeactivate()}
        onCancel={() => setDeactivateTarget(null)}
      />
    </DashboardLayout>
  );
};

export default StaffPage;
